import { useEffect, useId, useState } from 'react';
import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTranslation } from '../../i18n/useTranslation';
import { useTheme } from '../../hooks/useTheme';
import { Button } from '../atoms/Button/Button';
import { Inline } from '../atoms/Inline/Inline';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Select } from '../atoms/Select/Select';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { Dialog } from '../ui/Dialog/Dialog';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { readCssVar, usePrefersReducedMotion } from '../measurements/historyChartUtils';
import type { SensorMapping } from '../../api/controller';
import { useSensorReadings } from '../../hooks/useController';
import { formatDateTime, formatValue, mappingName } from './controllerFormat';
import './Controller.css';

const RANGES = [7, 30, 90, 365] as const;

interface ChartColours {
  line: string;
  band: string;
  grid: string;
  axis: string;
}

function useChartColours(): ChartColours {
  const { theme } = useTheme();
  const read = (): ChartColours => ({
    line: readCssVar('--color-primary'),
    band: readCssVar('--color-status-infoSubtle'),
    grid: readCssVar('--color-border-secondary'),
    axis: readCssVar('--color-text-muted'),
  });
  const [colours, setColours] = useState<ChartColours>(read);
  useEffect(() => setColours(read()), [theme]);
  return colours;
}

export interface SensorHistoryDialogProps {
  aquariumId: string;
  mapping: SensorMapping;
  onClose: () => void;
}

/** Average of each window as a line, its min–max as a band; plus a text summary of the period. */
export function SensorHistoryDialog({ aquariumId, mapping, onClose }: SensorHistoryDialogProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'de';
  const id = useId();
  const [days, setDays] = useState<number>(30);
  const readings = useSensorReadings(aquariumId, mapping.id, days);
  const colours = useChartColours();
  const reducedMotion = usePrefersReducedMotion();
  const name = mappingName(mapping);
  const unit = readings.data?.mapping.unit ?? mapping.unit;

  const rows = (readings.data?.readings ?? []).map((r) => ({
    time: new Date(r.measuredAt).getTime(),
    value: r.value,
    band: r.min !== null && r.max !== null ? [r.min, r.max] : null,
  }));
  const values = (readings.data?.readings ?? []).flatMap((r) => [r.value, r.min ?? r.value, r.max ?? r.value]);
  const low = values.length > 0 ? Math.min(...values) : null;
  const high = values.length > 0 ? Math.max(...values) : null;
  const latest = readings.data?.readings.at(-1);
  const dateTick = (time: number) => new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short' }).format(new Date(time));

  let body;
  if (readings.isPending) {
    body = (
      <LiveRegion busy>
        <Text color="muted">{t('controller.common.loading')}</Text>
      </LiveRegion>
    );
  } else if (readings.isError) {
    body = (
      <LoadErrorState
        title={t('controller.history.loadError')}
        titleLevel={3}
        onRetry={() => void readings.refetch()}
        retryLabel={t('controller.common.retry')}
      />
    );
  } else if (rows.length === 0) {
    body = <Text color="muted">{t('controller.history.empty')}</Text>;
  } else {
    body = (
      <Stack gap="3">
        <Text>
          {t('controller.history.summary', {
            latest: latest ? formatValue(latest.value, unit, language) : '–',
            low: low !== null ? formatValue(low, unit, language) : '–',
            high: high !== null ? formatValue(high, unit, language) : '–',
            count: rows.length,
          })}
        </Text>
        <div className="controller-history__chart" role="img" aria-label={t('controller.history.caption', { name, days })}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows}>
              <CartesianGrid stroke={colours.grid} strokeDasharray="3 3" />
              <XAxis dataKey="time" type="number" scale="time" domain={['dataMin', 'dataMax']} tickFormatter={dateTick} stroke={colours.axis} />
              <YAxis domain={['auto', 'auto']} stroke={colours.axis} width={48} />
              <Tooltip
                labelFormatter={(time) => formatDateTime(new Date(Number(time)).toISOString(), language)}
                formatter={(value) =>
                  Array.isArray(value)
                    ? `${formatValue(Number(value[0]), unit, language)} – ${formatValue(Number(value[1]), unit, language)}`
                    : formatValue(Number(value), unit, language)
                }
              />
              <Area
                dataKey="band"
                name={t('controller.history.band')}
                stroke="none"
                fill={colours.band}
                isAnimationActive={!reducedMotion}
                connectNulls
              />
              <Line
                dataKey="value"
                name={t('controller.history.value')}
                stroke={colours.line}
                strokeWidth={2}
                dot={rows.length <= 60}
                isAnimationActive={!reducedMotion}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {latest && (
          <Text color="muted" size="sm">
            {t('controller.history.lastReading', { time: formatDateTime(latest.measuredAt, language) })}
            {mapping.origin && ` · ${t('controller.history.origin', { origin: mapping.origin })}`}
          </Text>
        )}
        {readings.data?.aggregated === 'day' && (
          <Text color="muted" size="sm">{t('controller.history.daily')}</Text>
        )}
      </Stack>
    );
  }

  return (
    <Dialog isOpen onClose={onClose} title={t('controller.history.title', { name })} size="lg">
      <Stack gap="4">
        <div className="controller-history__range">
          <Select
            id={`${id}-range`}
            label={t('controller.history.range')}
            value={String(days)}
            options={RANGES.map((d) => ({ value: String(d), label: t(`controller.history.days${d}`) }))}
            onChange={(e) => setDays(Number(e.target.value))}
          />
        </div>
        {body}
        <Inline justify="end">
          <Button variant="secondary" onClick={onClose}>
            {t('controller.common.close')}
          </Button>
        </Inline>
      </Stack>
    </Dialog>
  );
}
